import React, {useEffect, useState} from 'react';
import {
    Box,
    Button,
    Card,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    IconButton,
    List,
    ListItem,
    Typography
} from "@mui/material";
import {ArrowDropDown, ArrowDropUp} from "@material-ui/icons";
import RemoveIcon from "@material-ui/icons/Remove";
import useStyles from "../../../assets/styles/OverviewStyles";
import ChatBotView from "../../../ui_library/ver_24.10/ChatBot/ChatBot.comp";

/**
 * View: ProgramOutline
 *
 * **Data Schema:**
 *
 * data = {
 *   units: Array,               // List of units within the program
 *     [
 *       {
 *         unitId: string,           // Unique identifier for the unit
 *         title: string,        // Unit title
 *         lessons: Array,       // List of lessons within the unit
 *           [
 *             {
 *               lessonId: string,     // Unique identifier for the lesson
 *               title: string,  // Lesson title
 *               studentOutcomes: Array // List of outcomes for the lesson
 *                 [
 *                   string,    // Description of the student outcome
 *                 ]
 *             }
 *           ]
 *       }
 *     ]
 * }
 *
 * **Actions:**
 * - `removeUnitAction(unitToRemoveIndex)`: Removes the unit from the data array based on the unitId.
 *
 * **Options:**
 *
 * options = {
 *   expandedUnits: string,    // Controls whether units are 'expanded' or 'collapsed'
 *   openDialog: boolean,      // Controls whether the remove dialog is open
 *   unitToRemove: null,       // controls whether the
 * }
 *
 * **Usage Example:**
 * <ProgramOutline data={data} actions={actions} options={options} />
 */

const TARGET_GROUP = {
    General: "General Public",
    CivilService: "Civil Service",
    Parents: "Parents",
    Teachers: "Teachers",
    EarlyYears: "Early Years",
    Children: "Children",
    Teens: "Teenagers",
    Youth: "Youth",
    Workforce: "Workforce",
    AcademicsResearchers: "Academics / Researchers",
    Gamers: "Gamers",
    PPP: "Public-Private Partnership",
    Women: "Women",
    Seniors: "Senior Citizens",
    SchoolLeaders: "School Leaders",
    StudentsK12: "K-12 School Students",
    StudentsUni: "University Students",
};

const TARGET_AGE = {
    Below5: "Ages 0-4",
    "5-7YO": "Ages 5-7",
    "8-9YO": "Ages 8-9",
    "10-12YO": "Ages 10-12",
    "13-14YO": "Ages 13-14",
    "15-18YO": "Ages 15-18",
    "19-24YO": "Ages 19-24",
    "25-34YO": "Ages 25-34",
    "35-44YO": "Ages 35-44",
    "45-54YO": "Ages 45-54",
    "55-64YO": "Ages 55-64",
    Above65: "Ages 65+",
    Under18: "Ages 5-18",
    "18-45YO": "Ages 18-45",
    "40-65YO": "Ages 40-65",
};

const ProgramOutline = ({data, actions, options}) => {
    const {programData, unitsData, modulesData} = data;
    const {removeUnit, removeModule,sendMessageAction} = actions;

    const classes = useStyles();
    const [expandedUnits, setExpandedUnits] = useState([]);
    const [expandedModules, setExpandedModules] = useState([]);
    const [expandedSessions, setExpandedSessions] = useState({});
    const [openDialog, setOpenDialog] = useState(false);
    const [itemToRemove, setItemToRemove] = useState({type: null, id: null});

    useEffect(() => {
        if (unitsData && unitsData.length > 0) {
            setExpandedUnits(unitsData.map((_, index) => index));
        }
    }, [unitsData]);

    const handleRemoveItem = (type, m_id, id) => {
        setItemToRemove({type, m_id, id});
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setItemToRemove({type: null, id: null});
    };

    const handleConfirmRemove = async () => {
        const {type, m_id, id} = itemToRemove;
        if (type === 'unit' && programData?.program_id) {
            await removeUnit(programData.program_id, m_id, id);
        } else if (type === 'module' && programData?.program_id) {
            await removeModule(programData.program_id, id);
        }
        handleCloseDialog();
    };

    const toggleUnit = (unitIndex) => {
        setExpandedUnits((prev) =>
            prev.includes(unitIndex) ? prev.filter(i => i !== unitIndex) : [...prev, unitIndex]
        );
    };

    const toggleModule = (moduleIndex) => {
        setExpandedModules((prev) =>
            prev.includes(moduleIndex) ? prev.filter(i => i !== moduleIndex) : [...prev, moduleIndex]
        );
    };

    const toggleSession = (unitIndex, sessionIndex) => {
        setExpandedSessions((prev) => ({
            ...prev,
            [unitIndex]: {
                ...prev[unitIndex],
                [sessionIndex]: !prev[unitIndex]?.[sessionIndex],
            },
        }));
    };

    const handleSubmit = () => {
        actions.sendMessageAction().then(() => {});
    };

    const {totalUnits, totalSessions} = modulesData.reduce((acc, module) => {
        acc.totalUnits += module.units.length;
        acc.totalSessions += module.units.reduce((unitTotal, unit) => {
            return unitTotal + (unit.lessons ? unit.lessons.length : 0);
        }, 0);
        return acc;
    }, {totalUnits: 0, totalSessions: 0});

    const getAudienceText = () => {
        const group_part = TARGET_GROUP[programData.target_group] || 'Grades 3-4';
        const age_part = TARGET_AGE[programData.target_age] || 'Ages 8-12';

        return `${group_part} (${age_part})`;
    }


    return (
        <>
            <Box sx={{minHeight: {xs: '95vh', sm: '87vh'}, width: {xs: '100vw', sm: '95vw'}}} style={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <div className={classes.container}>
                    <Typography variant="h4" className={classes.title}>
                        Your {programData.course_name} Program Overview
                    </Typography>

                    <div className={classes.cardsContainer}>
                        <Card className={classes.card}>
                            <Typography variant="h6">Audience</Typography>
                            <Typography variant="subtitle1">{getAudienceText()}</Typography>
                        </Card>
                        <Card className={classes.card}>
                            <Typography variant="h6">Program Structure</Typography>
                            <Typography variant="subtitle1">{totalUnits} Units / {totalSessions} Lessons</Typography>
                        </Card>
                        <Card className={classes.card}>
                            <Typography variant="h6">Program Length</Typography>
                            <Typography variant="subtitle1">{programData.course_duration / 60} min</Typography>
                        </Card>
                    </div>
                    <div className={classes.overviewContainer}>
                        <Typography variant="h6" style={{color: '#ED6628', marginRight: '10px'}}
                                    className={classes.overview}>
                            <b>Overview:</b>
                        </Typography>
                        <Typography variant="body1" style={{marginTop: '5px'}} className={classes.overview}>
                            {programData.course_goal}
                        </Typography>
                    </div>
                    <div className={classes.unitContainer}>
                        {modulesData.map((module, moduleIndex) => (
                            <div key={moduleIndex} className={classes.unit} style={{width: '100%'}}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: '100%',
                                    justifyContent: 'space-between'
                                }}>
                                    <Button
                                        onClick={() => toggleModule(moduleIndex)}
                                        style={{
                                            color: '#EE6628',
                                            fontWeight: 'bold',
                                            outline: 'none',
                                            boxShadow: 'none',
                                            fontSize: '18px',
                                            justifyContent: 'space-between',
                                            textAlign: 'left',
                                            flexGrow: 1,
                                        }}
                                        fullWidth
                                    >
                                        {`${module.module_name}`}
                                        {expandedModules.includes(moduleIndex) ? <ArrowDropUp/> : <ArrowDropDown/>}
                                    </Button>

                                    <IconButton
                                        onClick={() => handleRemoveItem('module', module.module_code, module.module_code)}
                                        style={{color: '#EE6628', marginLeft: '10px'}}
                                    >
                                        <RemoveIcon/>
                                    </IconButton>
                                </div>

                                <Collapse in={expandedModules.includes(moduleIndex)} timeout="auto" unmountOnExit>
                                    {module.units.map((unit, unitIndex) => (
                                        <div key={unitIndex} className={classes.unit}
                                             style={{marginLeft: '38px', width: '96%',}}>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between'
                                            }}>
                                                <Button
                                                    onClick={() => toggleUnit(unitIndex)}
                                                    style={{
                                                        color: '#e7456d',
                                                        fontWeight: 'bold',
                                                        outline: 'none',
                                                        boxShadow: 'none',
                                                        fontSize: '18px',
                                                        justifyContent: 'space-between',
                                                        textAlign: 'left',
                                                    }}
                                                    fullWidth
                                                >
                                                    {`${unit.unit_title} (${unit.unit_duration / 60} MINS TOTAL)`}
                                                    {expandedUnits.includes(unitIndex) ? <ArrowDropUp/> :
                                                        <ArrowDropDown/>}
                                                </Button>
                                                <IconButton
                                                    onClick={() => handleRemoveItem('unit', module.module_code, unit.unit_code)}
                                                    style={{color: '#EE6628', marginLeft: '10px'}}
                                                >
                                                    <RemoveIcon/>
                                                </IconButton>
                                            </div>
                                            <Collapse in={expandedUnits.includes(unitIndex)} timeout="auto"
                                                      unmountOnExit>
                                                <List>
                                                    {unit.lessons && unit.lessons.map((session, sessionIndex) => (
                                                        <div key={sessionIndex}>
                                                            <ListItem button
                                                                      onClick={() => toggleSession(unitIndex, sessionIndex)}>
                                                                <Typography variant="h6" className={classes.lesson}
                                                                            style={{marginLeft: '8px'}}>
                                                                    {`${session.lesson_title} (${session.lesson_duration / 60} mins)`}
                                                                </Typography>
                                                                {expandedSessions[unitIndex]?.[sessionIndex] ?
                                                                    <ArrowDropUp style={{color: '#EE6628'}}/> :
                                                                    <ArrowDropDown style={{color: '#EE6628'}}/>}
                                                            </ListItem>
                                                            <Collapse in={expandedSessions[unitIndex]?.[sessionIndex]}
                                                                      timeout="auto" unmountOnExit>
                                                                {session.student_outcomes && (
                                                                    <List>
                                                                        <ListItem style={{paddingTop: '0px'}}>
                                                                            <Typography variant="h6" style={{
                                                                                color: '#6A8EC9',
                                                                                marginLeft: '27px'
                                                                            }}>
                                                                                Session Outcomes:
                                                                            </Typography>
                                                                        </ListItem>
                                                                        <ul style={{
                                                                            marginLeft: '40px',
                                                                            listStyleType: 'disc'
                                                                        }}>
                                                                            {Array.isArray(session.student_outcomes) ? (
                                                                                session.student_outcomes.map((outcome, outcomeIndex) => (
                                                                                    <li key={outcomeIndex} style={{
                                                                                        marginBottom: '10px',
                                                                                        color: '#606060'
                                                                                    }}>
                                                                                        {outcome.goal}
                                                                                    </li>
                                                                                ))
                                                                            ) : (
                                                                                <li style={{
                                                                                    marginBottom: '10px',
                                                                                    color: '#606060'
                                                                                }}>
                                                                                    {session.student_outcomes}
                                                                                </li>
                                                                            )}
                                                                        </ul>
                                                                    </List>
                                                                )}
                                                            </Collapse>
                                                        </div>
                                                    ))}
                                                </List>
                                            </Collapse>
                                        </div>
                                    ))}
                                </Collapse>
                            </div>
                        ))}
                    </div>
                </div>
                <div className={classes.chatBotContainer}>
                    <Typography variant="subtitle1" style={{marginBottom: '8px', marginLeft:'10px',}}>
                        Click Submit to preview your online program.
                    </Typography>
                    <div style={{display: 'flex'}}>
                        <ChatBotView
                            data={data}
                            actions={actions}
                            options={options}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSubmit}
                            style={{ backgroundColor: '#EE6628',height:'45px', boxShadow:'none'}}
                        >
                            Submit
                        </Button>
                    </div>
                </div>
            </Box>

            <Dialog open={openDialog} onClose={handleCloseDialog}>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to remove this unit?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} color="primary">Cancel</Button>
                    <Button onClick={handleConfirmRemove} color="primary">Yes</Button>
                </DialogActions>
            </Dialog>
        </>
    )
};

export default ProgramOutline
